import React, { useState, useEffect } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const STORAGE_KEY = 'tabbibak_posologie_poids_enfant';

const PediatricDosage = () => {
  const { t } = useLanguage();
  const [weight, setWeight] = useState('');
  const [ageMonths, setAgeMonths] = useState('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setWeight(saved);
    } catch (e) {
      // localStorage indisponible (navigation privée, etc.) - on ignore simplement
    }
  }, []);

  const w = parseFloat(weight);
  const ageNum = parseInt(ageMonths, 10);
  const hasAge = ageMonths !== '' && !Number.isNaN(ageNum);
  const inRange = w > 0 && w >= 3 && w <= 50;
  const outOfRange = w > 0 && !inRange;

  // Paracetamol : 15 mg/kg/prise, toutes les 6h, max 4 prises/24h, plafond absolu 1g/prise
  const paraDoseMg = inRange ? Math.min(Math.round(w * 15), 1000) : null;

  // Ibuprofene (sirop 2% = 20 mg/ml) : 10 mg/kg/prise, toutes les 8h, max 3 prises/24h
  const ibuAllowed = inRange && (!hasAge || ageNum >= 3);
  const ibuDoseMg = ibuAllowed ? Math.round(w * 10) : null;
  const ibuDoseMl = ibuAllowed ? (w / 2).toFixed(1) : null;

  return (
    <ToolPageLayout
      title={t('pediatric_title')}
      subtitle={t('pediatric_subtitle')}
      specialty="pediatre"
    >
      <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3 mb-8">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('pediatric_warning')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('pediatric_weight_label')}</Label>
          <Input
            type="number"
            min="1"
            max="80"
            step="0.5"
            value={weight}
            onChange={(e) => {
              setWeight(e.target.value);
              try {
                if (e.target.value) localStorage.setItem(STORAGE_KEY, e.target.value);
                else localStorage.removeItem(STORAGE_KEY);
              } catch (err) {
                // localStorage indisponible - on ignore simplement
              }
            }}
            placeholder={t('pediatric_weight_placeholder')}
            className="rounded-xl"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('pediatric_age_label')}</Label>
          <Input
            type="number"
            min="0"
            max="216"
            value={ageMonths}
            onChange={(e) => setAgeMonths(e.target.value)}
            placeholder={t('pediatric_age_placeholder')}
            className="rounded-xl"
          />
        </div>
      </div>

      {outOfRange && (
        <p className="text-sm text-[#EF4444] mb-6">
          {t('pediatric_out_of_range')}
        </p>
      )}

      {inRange && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-5 border border-[#E2E8F0] rounded-2xl bg-[#F8FAFC] space-y-2">
            <h3 className="font-bold text-[#0F172A] text-base">{t('pediatric_para_heading')}</h3>
            <p className="text-xs text-[#64748B]">{t('pediatric_para_desc')}</p>
            <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] space-y-1">
              <p className="text-sm text-[#64748B]">
                {t('pediatric_dose_label')} <strong className="text-[#0596DE]">{paraDoseMg} mg</strong>
              </p>
              <p className="text-[11px] text-[#94A3B8]">
                {t('pediatric_para_max')}
              </p>
            </div>
          </div>

          <div className="p-5 border border-[#E2E8F0] rounded-2xl bg-[#F8FAFC] space-y-2">
            <h3 className="font-bold text-[#0F172A] text-base">{t('pediatric_ibu_heading')}</h3>
            <p className="text-xs text-[#64748B]">
              {t('pediatric_ibu_desc')}
            </p>
            {ibuAllowed ? (
              <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] space-y-1">
                <p className="text-sm text-[#64748B]">
                  {t('pediatric_dose_label')} <strong className="text-[#0596DE]">{ibuDoseMg} mg</strong> {t('pediatric_dose_approx_ml')}{' '}
                  <strong className="text-[#0596DE]">{ibuDoseMl} ml</strong>
                </p>
                <p className="text-[11px] text-[#94A3B8]">
                  {t('pediatric_ibu_max')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-[#EF4444] font-medium">
                {t('pediatric_ibu_not_recommended')}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 bg-[#FEE2E2] border border-[#EF4444]/30 rounded-2xl p-4 flex gap-3">
        <ShieldAlert className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
        <p className="text-sm text-[#991B1B]">
          <strong>{t('pediatric_contraindication_title')}</strong> {t('pediatric_contraindication_text')}
        </p>
      </div>

      <p className="mt-6 text-sm text-[#64748B]">
        {t('pediatric_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default PediatricDosage;
